<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import categoryService from '@/services/category.service'
import CategoryDrawer from '@/views/feedback/master/CategoryDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-category',
    subject: 'feedback.menu',
    action: 'master_category',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const isCategoryDrawerVisible = ref(false)
const categoryDrawerData = ref()
const categoryDeleteData = ref()
const categoryDrawerType = ref('Add')

const refImportCategoryForm = ref()
const importCategoryFile = ref()
const importCategoryErrors = ref()
const isImportCategoryLoading = ref(false)
const isImportCategoryModal = ref(false)
const snackbar = useToast()


const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
   
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

const selectedParentCategory = ref()

const selectedDepartment = ref()


// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Department',
    key: 'department_id',
  },
  {
    title: 'Parent Category',
    key: 'parent_id',
  },
  {
    title: 'Priority',
    key: 'priority',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const { data: departments, isFetching: isFetchingDepartment } = await useApi(
  createUrl("masters/departments", {
    query: {
      select: ["id", "name"],
    },
  }),
)

const {
  data: categoriesData,
  execute: fetchCategories,
  isFetching: isFetchingCategories,
} = useApi(createUrl('/masters/categories', {
  query: computed(() => {
    const baseQuery = {
      q: debouncedSearch.value,
      sort: sortBy.value,
      order_by: orderBy.value,
      search: {
        department_id: selectedDepartment.value,
        parent_id: selectedParentCategory.value,
      },
    }

    if (itemsPerPage.value !== -1) {
      return {
        ...baseQuery,
        paged: 1,
        itemsPerPage: itemsPerPage.value,
        page: page.value,
      }
    }

    return baseQuery
  }),
}))

const categories = computed(() => {
  // When fetching all data
  if (itemsPerPage.value === -1) {
    return categoriesData.value || []
  }

  // Normal paginated response
  return categoriesData.value?.data || []
})

const totalCategories = computed(() => {
  // For "All" selection
  if (itemsPerPage.value === -1) {
    return categories.value.length 
  }

  // For paginated results
  return categoriesData.value?.meta?.total || 0
})

const updateCategory = data => {
  if (data.id){
    categoryService.updateCategory(data.id, data, ()=>
    {
      fetchCategories()
      isCategoryDrawerVisible.value = false
      resetErrors()
    }, onError)
   
  }
}

const saveCategory = data => {
  categoryService.addCategory(data, ()=>
  {
    fetchCategories()
    isCategoryDrawerVisible.value = false
    resetErrors()

  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}


const showCategory = (categoryData, type) => {
  categoryDrawerData.value = { ...categoryData }
  categoryDrawerType.value = type
  isCategoryDrawerVisible.value = true
}

const deleteCategory = async (confirm, data) => {
  if(data?.id && confirm ){
    await categoryService.deleteCategory(data.id, () => {
      fetchCategories()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = categoryData => {
  categoryDeleteData.value = categoryData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    categoryDeleteData.value = {}
  }
})

const computedParentCategorySearch = computed(()=>{
  return selectedDepartment.value
})

const { data: parentCategories, isFetching: isFetchingParentCategories } = await useApi(
  createUrl("masters/categories", {
    query: {
      parent_id: 'null',
      'search[department_id]': computedParentCategorySearch,
    },
  }),
)

const submitImportCategory = async () => {
  if (!importCategoryFile.value) {
    importCategoryErrors.value = {
      file: ["Please select a file to import."],
    }

    return
  }
  isImportCategoryLoading.value = true
  importCategoryErrors.value = null // Clear previous errors

  try {
    await categoryService.import(
      {
        file: importCategoryFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Categories imported successfully.")
        isImportCategoryModal.value = false
        fetchCategories() // Refresh the list
        importCategoryFile.value = null // Reset file input
      },
      error => {
        // onError callback
        console.log(error._data?.message)
        importCategoryErrors.value = error.data?.errors || error._data?.message || {
          message:
            error.data?.message ||  error._data?.message || "File upload failed. Please try again.",
        }

        //snackbar.error(error.data?.message || error._data?.message || "File upload failed.")
      },
    )
  } catch (e) {
    const errorData = e.data

    importCategoryErrors.value = errorData?.errors || {
      message: errorData?.message || "File upload failed. Please try again.",
    }
    snackbar.error(
      errorData?.message || "An unexpected error occurred during import.",
    )
    if (!importCategoryErrors.value && !errorData?.message)
      importCategoryErrors.value = { message: "Import submission failed." }
  } finally {
    isImportCategoryLoading.value = false
  }
}

const downloadSampleCategoryFile = async () => {
  await downloadFile(
    "masters/categories/sample-import-file/export",
    "categories-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

function getPriorityColor(priority) {
  if (!priority) return 'grey'

  switch (priority.toLowerCase()) {
  case 'high':
    return 'error'
  case 'medium':
    return 'warning'
  case 'low':
    return 'success'
  default:
    return 'grey'
  }
}
function capitalizeFirst(value) {
  if (!value) return ''
  
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

watch(
  () => isImportCategoryModal.value,
  value => {
    if (value === false) {
      importCategoryFile.value = null
      importCategoryErrors.value = null
    }
  },
)
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <!-- 👉 Left side: Filters -->
        <div class="d-flex align-center flex-wrap gap-4 filter-section">
          <div class="filter-field filter-field--search">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
          <div class="filter-field">
            <AppSelect
              v-model="selectedDepartment"
              :loading="isFetchingDepartment"
              :items="departments"
              :item-title="(item) => item?.name"
              :item-value="(item) => item?.id"
              clearable
              placeholder="Filter Departments"
            />
          </div>
          <div class="filter-field">
            <AppSelect
              v-model="selectedParentCategory"
              :loading="isFetchingParentCategories"
              :items="parentCategories"
              :item-title="(item) => item?.name"
              :item-value="(item) => item?.id"
              clearable
              placeholder="Filter Parent Category"
            />
          </div>
        </div>

        <VSpacer />

        <!-- 👉 Right side: Action Buttons -->
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            v-if="$can('create', 'master.category')"
            variant="tonal"
            prepend-icon="tabler-download"
            @click="isImportCategoryModal = !isImportCategoryModal"
          >
            Import
          </VBtn>

          <VBtn
            v-if="$can('create', 'master.category')"
            prepend-icon="tabler-plus"
            variant="flat"
            @click="showCategory({}, 'Add')"
          >
            Add Category
          </VBtn>

          <IconBtn
            variant="tonal"
            class="rounded"
            :loading="isFetchingCategories"
            @click="fetchCategories"
          >
            <VIcon
              size="24"
              icon="tabler-refresh"
            />
          </IconBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="categories"
        :items-length="totalCategories"
        :headers="headers"
        :loading="isFetchingCategories"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="text-base font-weight-medium cursor-pointer"
            @click="showCategory(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <template #item.parent_id="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.parent?.name ?? 'N/A' }}</span>
        </template>

        <template #item.department_id="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.department?.name }}</span>
        </template>

        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>

        <template #item.priority="{ item }">
          <VChip
            v-if="item.priority"
            size="x-small"
            :color="getPriorityColor(item.priority)"
            variant="outlined"
          >
            {{ capitalizeFirst(item.priority) }}
          </VChip>
        </template>

        <template #item.status="{ item }">
          <VChip
            size="x-small"
            :color="item.status ? 'primary' : 'secondary'"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.category')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.category')"
            @click="showCategory(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>  
    <CategoryDrawer
      v-model:isDrawerOpen="isCategoryDrawerVisible"
      :category-data="categoryDrawerData"
      :type="categoryDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="categoryDrawerType = $event"
      @update:category-data="updateCategory"
      @save-category="saveCategory"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this category?"
      confirm-msg="Category has been deleted successfully."
      confirm-title="Category Deleted!"
      :data="categoryDeleteData"
      @confirm="deleteCategory"
    />

    <!-- Import Category Modal -->
    <VDialog
      v-model="isImportCategoryModal"
      max-width="600"
      :persistent="isImportCategoryLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportCategoryLoading"
        @click="isImportCategoryModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Categories
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportCategoryForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportCategoryLoading"
              :error-messages="importCategoryErrors?.file"
              @change="
                (e) => {
                  importCategoryFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importCategoryErrors?.message &&
              !Array.isArray(importCategoryErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importCategoryErrors.message = null"
          >
            {{ importCategoryErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText v-if="Array.isArray(importCategoryErrors) && importCategoryErrors.length > 0">  
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-error text-caption font-weight-medium">
              {{ importCategoryErrors.length }} 
              {{ importCategoryErrors.length === 1 ? 'Error' : 'Errors' }} found
            </div>
            <VBtn
              icon
              color="error"
              variant="text"
              size="small"
              class="ma-0 pa-0"
              density="compact"
              @click="importCategoryErrors = []"
            >
              <VIcon
                color="error"
                size="18"
                icon="tabler-x"
              />
            </VBtn>
          </div>

          <div
            class="d-flex flex-column gap-2 scrollable-errors"
            style="max-height: 90px; overflow-y: auto;"
          >
            <VAlert
              v-for="(error, index) in importCategoryErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
              style="flex-shrink: 0;"
            >
              {{ error.row ? `Row ${error.row}: ` : "" }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>


        <!-- Loading Bar -->
        <VCardText
          v-if="isImportCategoryLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="primary"
            :disabled="isImportCategoryLoading"
            @click="downloadSampleCategoryFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="#EF5350"
            :disabled="isImportCategoryLoading"
            @click="isImportCategoryModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="flat"
            :loading="isImportCategoryLoading"
            :disabled="isImportCategoryLoading || !importCategoryFile"
            @click="submitImportCategory"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.filter-field {
  inline-size: 15rem;
}

.filter-field--search {
  inline-size: 10rem;
}

@media (max-width: 600px) {
  .filter-section {
    inline-size: 100%;
  }

  .filter-field,
  .filter-field--search {
    inline-size: 100%;
  }
}

:deep(.v-data-table-footer) {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}

/* For Chrome, Edge, Safari */
.scrollable-errors::-webkit-scrollbar {
  width: 2px;               /* thin scrollbar */
}

.scrollable-errors::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);  /* light color */
  border-radius: 2px;
}

.scrollable-errors::-webkit-scrollbar-track {
  background-color: transparent;      /* keep track transparent */
}

/* For Firefox */
.scrollable-errors {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}
</style>
